import { Menu, X, ArrowRight, LayoutGrid, Database, Users, BarChart3 } from "lucide-react";
import { COLORS, ASSETS } from "../styles/tokens";
import { useLockBodyScroll } from "../hooks";

export type TabId = "explorer" | "metadata" | "coverage" | "onboard";

const NAV_ITEMS = [
  { id: "explorer" as TabId,  label: "Signal Explorer",   desc: "Browse signal packages", Icon: LayoutGrid },
  { id: "metadata" as TabId,  label: "Schema & Metadata", desc: "Schema & governance",    Icon: Database   },
  { id: "coverage" as TabId,  label: "Coverage",          desc: "Live bid coverage",      Icon: BarChart3  },
  { id: "onboard"  as TabId,  label: "Onboarding",        desc: "Get a seat mapped",      Icon: Users      },
];

// ─── Desktop sidebar ─────────────────────────────────────────────────────────

interface SidebarProps {
  tab: TabId;
  setTab: (tab: TabId) => void;
  totalSignals: number;
}

export function Sidebar({ tab, setTab, totalSignals }: SidebarProps) {
  return (
    <aside
      className="glass"
      style={{
        width: 220,
        flexShrink: 0,
        height: "100%",
        overflowY: "auto",
        borderRight: `1px solid ${COLORS.border}`,
        display: "flex",
        flexDirection: "column",
        zIndex: 10,
      }}
    >
      {/* Navigation */}
      <nav style={{ padding: "16px 12px 4px", flex: 1 }}>
        <div
          className="mono"
          style={{ fontSize: 9, color: COLORS.inkMute, letterSpacing: "0.15em", padding: "0 8px 10px" }}
        >
          NAVIGATION
        </div>
        {NAV_ITEMS.map(({ id, label, Icon }) => {
          const isActive = tab === id;
          return (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                borderRadius: 6,
                border: "none",
                borderLeft: isActive ? `2px solid ${COLORS.blue}` : "2px solid transparent",
                background: isActive ? `${COLORS.blue}14` : "transparent",
                color: isActive ? COLORS.ink : COLORS.inkDim,
                fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s",
                marginBottom: 2,
              }}
            >
              <Icon size={15} color={isActive ? COLORS.blue : COLORS.inkMute} strokeWidth={2} />
              {label}
            </button>
          );
        })}
      </nav>

      {/* Status footer */}
      <div style={{ padding: "16px 20px", borderTop: `1px solid ${COLORS.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="pulse-dot" />
          <span className="mono" style={{ fontSize: 11, color: COLORS.inkDim, letterSpacing: "0.05em" }}>
            LIVE · {totalSignals} SIGNALS
          </span>
        </div>
      </div>
    </aside>
  );
}

// ─── Mobile top bar ───────────────────────────────────────────────────────────

interface HeaderProps {
  tab: TabId;
  setTab: (tab: TabId) => void;
  totalSignals: number;
  navOpen: boolean;
  setNavOpen: (open: boolean) => void;
  isMobile: boolean;
}

const SHORT_LABELS: Record<TabId, string> = {
  explorer: "Explorer",
  metadata: "Metadata",
  coverage: "Coverage",
  onboard: "Onboarding",
};

export function Header({ tab, setNavOpen }: HeaderProps) {
  return (
    <header
      className="layer glass"
      style={{
        borderBottom: `1px solid ${COLORS.border}`,
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 50,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          className="badge-float"
          style={{
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            filter: `drop-shadow(0 4px 14px ${COLORS.cyan}40)`,
            flexShrink: 0,
          }}
        >
          <img
            src={ASSETS.badge}
            alt="Leanback Digital"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </div>
        <div>
          <div className="mono" style={{ fontSize: 9, color: COLORS.inkMute, letterSpacing: "0.15em" }}>
            LEANBACK DATA
          </div>
          <div className="display" style={{ fontSize: 15, fontWeight: 700 }}>
            RealSynq<span style={{ color: COLORS.cyan }}>.</span>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          className="mono"
          style={{
            fontSize: 10,
            color: COLORS.cyan,
            letterSpacing: "0.1em",
            padding: "5px 10px",
            borderRadius: 999,
            background: `${COLORS.cyan}12`,
            border: `1px solid ${COLORS.cyan}26`,
            whiteSpace: "nowrap",
          }}
        >
          {SHORT_LABELS[tab]}
        </span>
        <button
          onClick={() => setNavOpen(true)}
          style={{
            background: "rgba(10, 19, 32, 0.6)",
            border: `1px solid ${COLORS.border}`,
            color: COLORS.ink,
            width: 44,
            height: 44,
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
      </div>
    </header>
  );
}

// ─── Mobile full-screen nav overlay ──────────────────────────────────────────

interface MobileNavProps {
  tab: TabId;
  setTab: (tab: TabId) => void;
  onClose: () => void;
}

export function MobileNav({ tab, setTab, onClose }: MobileNavProps) {
  useLockBodyScroll(true);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(5, 9, 15, 0.96)",
        backdropFilter: "blur(20px)",
        display: "flex",
        flexDirection: "column",
        animation: "fadeUp 0.25s ease-out",
      }}
    >
      <div
        style={{
          padding: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${COLORS.border}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src={ASSETS.badge} alt="" style={{ width: 32, height: 32 }} />
          <div>
            <div className="mono" style={{ fontSize: 9, color: COLORS.inkMute, letterSpacing: "0.15em" }}>
              LEANBACK DATA
            </div>
            <div className="display" style={{ fontSize: 15, fontWeight: 700 }}>
              RealSynq<span style={{ color: COLORS.cyan }}>.</span>
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "transparent",
            border: `1px solid ${COLORS.border}`,
            color: COLORS.ink,
            width: 44,
            height: 44,
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
      </div>

      <nav style={{ padding: 24, display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
        {NAV_ITEMS.map(({ id, label, desc, Icon }, i) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            style={{
              padding: "20px",
              borderRadius: 10,
              border: `1px solid ${tab === id ? `${COLORS.cyan}44` : COLORS.border}`,
              background: tab === id ? `${COLORS.cyan}10` : "rgba(15, 26, 44, 0.6)",
              color: COLORS.ink,
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              gap: 14,
              animation: `fadeUp 0.4s ${i * 0.06}s ease-out both`,
            }}
          >
            <Icon size={18} color={tab === id ? COLORS.cyan : COLORS.inkMute} strokeWidth={2} />
            <div style={{ flex: 1 }}>
              <div className="display" style={{ fontSize: 20, fontWeight: 700, marginBottom: 2 }}>
                {label}
              </div>
              <div style={{ fontSize: 13, color: COLORS.inkDim }}>{desc}</div>
            </div>
            <ArrowRight size={18} color={tab === id ? COLORS.cyan : COLORS.inkMute} />
          </button>
        ))}
      </nav>

      <div style={{ padding: 24, borderTop: `1px solid ${COLORS.border}` }}>
        <div className="mono" style={{ fontSize: 10, color: COLORS.inkMute, letterSpacing: "0.15em", marginBottom: 8 }}>
          STATUS
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="pulse-dot" />
          <span className="mono" style={{ fontSize: 12, color: COLORS.inkDim }}>All systems operational</span>
        </div>
      </div>
    </div>
  );
}

// ─── Desktop sticky top navigation bar ───────────────────────────────────────

interface TopNavProps {
  totalSignals: number;
}

export function TopNav({ totalSignals }: TopNavProps) {
  return (
    <header
      className="glass"
      style={{
        flexShrink: 0,
        zIndex: 50,
        borderBottom: `1px solid ${COLORS.border}`,
        padding: "0 24px",
        height: 72,
        display: "flex",
        alignItems: "center",
        gap: 0,
      }}
    >
      {/* Branding — round floating badge (matches sidebar style) */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
        <div
          className="badge-float"
          style={{
            width: 40,
            height: 40,
            flexShrink: 0,
            filter: `drop-shadow(0 4px 14px ${COLORS.cyan}40)`,
          }}
        >
          <img
            src={ASSETS.badge}
            alt="Leanback Digital"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </div>
        <div>
          <div className="display" style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.1, color: COLORS.ink }}>
            Leanback Data
          </div>
          <div className="mono" style={{ fontSize: 9, color: COLORS.blue, letterSpacing: "0.12em", marginTop: 3 }}>
            REALSYNQ SIGNALS
          </div>
        </div>
      </div>

      {/* Live status — matches LID's "● Live data" right cluster */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <div className="pulse-dot" />
        <span style={{ fontSize: 13, color: COLORS.inkDim }}>
          Live data · {totalSignals} signals
        </span>
      </div>
    </header>
  );
}
